import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { delay, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { PlatformService } from '@core/services/platform/platform.service';

@Directive({
    selector: '[fixImageHeightSafari]',
})
export class FixImageHeightDirective implements AfterViewInit, OnDestroy {
    private resizeObserver: ResizeObserver;

    private readonly widthChange = new Subject<{ width: number; height: number }>();

    public widthChange$ = this.widthChange.asObservable();

    private readonly onDestroy$ = new Subject();

    @Input()
    public aspectRatioSize: { width: number; height: number };

    constructor(
        private readonly elRef: ElementRef,
        private readonly router: Router,
        private readonly platform: PlatformService,
    ) { }

    public async ngAfterViewInit() {
        if (this.platform.isBrowser()) {
            const img: HTMLImageElement = await this.elRef.nativeElement;
            const route = this.router.url;
            if (route.includes('lookbook')) {
                this.resizeObserver = new ResizeObserver((_) => {
                    this.widthChange.next({ width: img.width, height: img.height });
                });
                this.resizeObserver.observe(img);

                this.widthChange$.pipe(
                    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
                    delay(100),
                    takeUntil(this.onDestroy$)
                ).subscribe(() => {
                    this.updateSafariFixHeight(img);
                });
            }
        }
    }

    private updateSafariFixHeight(img: HTMLImageElement) {
        // default height 100%
        if (img.width === this.aspectRatioSize.width && img.height === this.aspectRatioSize.height) {
            img.style.removeProperty('--img-height');
            return;
        }
        if (img.height > 0) {
            img.style.setProperty('--img-height', img.height.toString() + 'px');
        } else {
            const sliceFlex: HTMLElement = img.closest('piece-image, piece-video');
            img.style.setProperty('--img-height', sliceFlex.offsetHeight.toString() + 'px');
        }
    }

    public ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
}
